// features/workstations/qqq/today/news-feed-card.js
// ═══════════════════════════════════════════════════════════
// News Feed Card with whisperer flagging
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_DASHBOARD_REBUILD_SPEC.md Section IV-B.
// Consumes /api/qqq/news/recent (Phase 2a-1).
//
// Renders the last 20 news items with:
//   - Tag filters (All / Whisperer / Fed / Earnings / Tech / Macro / Geopol)
//   - Source filter
//   - Star/unstar (persists via /api/qqq/news/star)
//   - Whisperer items highlighted with prominent badge
//
// Auto-refreshes every 60 seconds (news cron updates every 5 min).
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const TAG_FILTERS = [
  { id: null,            label: 'All',         tone: 'gold' },
  { id: 'whisperer',     label: 'Whisperer',   tone: 'terra' },
  { id: 'fed',           label: 'Fed',         tone: 'gold' },
  { id: 'earnings',      label: 'Earnings',    tone: 'sage' },
  { id: 'tech',          label: 'Tech',        tone: 'sage' },
  { id: 'macro',         label: 'Macro',       tone: 'gold' },
  { id: 'geopolitical',  label: 'Geopol',      tone: 'terra' },
];

export default function NewsFeedCard({ userId }) {
  const [items, setItems] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starring, setStarring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (tagFilter) params.append('tag', tagFilter);
      const res = await fetch(`/api/qqq/news/recent?${params}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setItems(json.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tagFilter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const toggleStar = async (item) => {
    if (!userId) return;
    setStarring(item.id);
    try {
      await fetch('/api/qqq/news/star', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          news_item_id: item.id,
          user_id: userId,
          starred: !item.starred,
        }),
      });
      // Optimistic update
      setItems(prev => prev.map(i =>
        i.id === item.id ? { ...i, starred: !i.starred, starred_by_self: !i.starred_by_self } : i
      ));
    } catch (_starErr) {
      // ignore errors silently for now (Phase 2b-2 adds toast)
    } finally {
      setStarring(null);
    }
  };

  return (
    <div className="qqq-news-feed-card">
      <div className="qqq-news-feed-card__header">
        <div className="qqq-news-feed-card__title">News Feed</div>
        <div className="qqq-news-feed-card__filters">
          {TAG_FILTERS.map(f => (
            <button
              key={f.id || 'all'}
              type="button"
              className={
                'qqq-news-feed-card__filter-btn' +
                (tagFilter === f.id ? ' qqq-news-feed-card__filter-btn--active' : '') +
                ` qqq-news-feed-card__filter-btn--${f.tone}`
              }
              onClick={() => setTagFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="qqq-news-feed-card__error">Error: {error}</div>}
      {loading && items.length === 0 && <div className="qqq-news-feed-card__loading">Loading…</div>}

      <div className="qqq-news-feed-card__items">
        {items.map(item => (
          <NewsItem
            key={item.id}
            item={item}
            onToggleStar={() => toggleStar(item)}
            starring={starring === item.id}
          />
        ))}
      </div>

      {!loading && items.length === 0 && (
        <div className="qqq-news-feed-card__empty">
          No items{tagFilter ? ` tagged "${tagFilter}"` : ''}.
        </div>
      )}
    </div>
  );
}

function NewsItem({ item, onToggleStar, starring }) {
  const isWhisperer = item.tags?.includes('whisperer');
  const itemClass =
    'qqq-news-feed-card__item' +
    (isWhisperer ? ' qqq-news-feed-card__item--whisperer' : '');

  return (
    <div className={itemClass}>
      <div className="qqq-news-feed-card__item-header">
        <span className="qqq-news-feed-card__source">{item.source.toUpperCase()}</span>
        {item.byline && <span className="qqq-news-feed-card__byline">— {item.byline}</span>}
        {isWhisperer && (
          <span className="qqq-news-feed-card__whisperer-badge" title="Known Fed-whisperer journalist">
            WHISPERER
          </span>
        )}
        <span className="qqq-news-feed-card__time">
          {item.published_at ? formatRelative(item.published_at) : ''}
        </span>
        <button
          type="button"
          className={
            'qqq-news-feed-card__star-btn' +
            (item.starred ? ' qqq-news-feed-card__star-btn--starred' : '')
          }
          onClick={onToggleStar}
          disabled={starring}
          title={item.starred ? 'Unstar' : 'Star (adds to observations log in Phase 2b-2)'}
        >
          {item.starred ? '★' : '☆'}
        </button>
      </div>
      <div className="qqq-news-feed-card__headline">
        {item.source_url ? (
          <a href={item.source_url} target="_blank" rel="noopener noreferrer">
            {item.headline}
          </a>
        ) : (
          item.headline
        )}
      </div>
      {item.summary && (
        <div className="qqq-news-feed-card__summary">{item.summary}</div>
      )}
      <div className="qqq-news-feed-card__tags">
        {(item.tags || []).map(t => (
          <span
            key={t}
            className={`qqq-news-feed-card__tag qqq-news-feed-card__tag--${t}`}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatRelative(iso) {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
